/**
 * RouteErrorBoundary — Iter 159 (Josh, after Lisa blank /contacts on prod 2026-06-03).
 *
 * Wraps every authenticated route so a runtime error in one page no longer
 * blanks the entire app. Instead the user sees an actionable "Something
 * went wrong — click to reset" card with three escape hatches:
 *
 *   1. "Try again"          — re-renders the same route from scratch.
 *   2. "Reset filters"      — wipes the most common offender (saved lead
 *                             filters in localStorage) then re-tries.
 *   3. "Go to Dashboard"    — navigates home, last-resort escape.
 *
 * The technical detail (error name + message + stack) is hidden under a
 * collapsible <details> so non-technical reps don't see a wall of code,
 * but support can ask the user to copy/paste it for diagnosis.
 *
 * The boundary auto-resets when the route changes — so a user who picks
 * "Go to Dashboard" sees the dashboard cleanly, and coming back to the
 * broken page re-tries it on its own.
 */
import React from "react";
import { AlertTriangle, RotateCw, Home, Filter } from "lucide-react";

const STORAGE_KEYS_TO_CLEAR = [
  "eh.globalLeadFilters.v1",
  "eh.leadFiltersDraft",
  "eh.contactFiltersDraft",
  "eh.dialerFiltersDraft",
];

class RouteErrorBoundary extends React.Component {
  state = { hasError: false, error: null, info: null, locationKey: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Best-effort telemetry — leave a trail in the browser console and
    // (if reachable) post to /api/telemetry/client-error so we can grep
    // for blanked pages across all reps.
    try {
      // eslint-disable-next-line no-console
      console.error("[RouteErrorBoundary] caught", {
        path: typeof window !== "undefined" ? window.location.pathname : "?",
        message: error?.message,
        stack: error?.stack,
        componentStack: info?.componentStack,
      });
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "") + "/api";
      const tok = (typeof localStorage !== "undefined" && (localStorage.getItem("token") || localStorage.getItem("access_token"))) || "";
      if (apiBase && tok) {
        // Fire-and-forget; ignore failure.
        fetch(`${apiBase}/telemetry/client-error`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${tok}` },
          body: JSON.stringify({
            path: window.location.pathname + window.location.search,
            message: String(error?.message || error || "unknown"),
            stack: String(error?.stack || "").slice(0, 4000),
            componentStack: String(info?.componentStack || "").slice(0, 4000),
            ua: navigator.userAgent,
            ts: new Date().toISOString(),
          }),
        }).catch(() => { /* offline — fine */ });
      }
    } catch { /* never let the boundary itself throw */ }
  }

  // Auto-reset on route change so navigation away clears the error.
  static getDerivedStateFromProps(props, state) {
    if (state.hasError && props.locationKey !== state.locationKey) {
      return { hasError: false, error: null, info: null, locationKey: props.locationKey };
    }
    if (!state.locationKey) return { locationKey: props.locationKey };
    return null;
  }

  retry = () => {
    this.setState({ hasError: false, error: null, info: null });
  };

  resetFiltersAndRetry = () => {
    try {
      STORAGE_KEYS_TO_CLEAR.forEach((k) => { try { localStorage.removeItem(k); } catch { /* quota */ } });
    } catch { /* sandboxed */ }
    this.retry();
  };

  goHome = () => {
    // Hard navigation so route guards re-run and any stuck state is gone.
    window.location.href = "/dashboard";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const err = this.state.error;
    const path = typeof window !== "undefined" ? window.location.pathname : "";
    return (
      <div
        data-testid="route-error-boundary"
        style={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, background: "var(--page-bg, #f7f7f8)" }}
      >
        <div style={{ maxWidth: 520, width: "100%", background: "#fff", borderRadius: 14, border: "0.5px solid #eee", boxShadow: "0 1px 2px rgba(0,0,0,0.04)", overflow: "hidden", fontFamily: "var(--font)" }}>
          <div style={{ padding: "20px 24px 12px", display: "flex", gap: 12, alignItems: "flex-start" }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(214, 158, 46, 0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <AlertTriangle style={{ width: 18, height: 18, color: "#B86E1A" }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 500, color: "var(--text-primary, #2b2b2f)" }}>Something went wrong — click to reset</div>
              <div style={{ fontSize: 12, color: "var(--text-tertiary, #6E6E72)", marginTop: 4, lineHeight: 1.5 }}>
                This page hit a runtime error. Your data is safe — nothing was saved or lost.
                {path && (
                  <>
                    {" "}Page: <code style={{ background: "#f4f4f5", padding: "1px 5px", borderRadius: 4, fontSize: 11 }}>{path}</code>
                  </>
                )}
              </div>
            </div>
          </div>

          <div style={{ padding: "8px 24px 20px", display: "flex", flexWrap: "wrap", gap: 8 }}>
            <button
              onClick={this.retry}
              data-testid="route-error-retry"
              style={{ padding: "8px 14px", borderRadius: 8, border: "none", background: "#1b365D", color: "#fff", fontSize: 12, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}
            >
              <RotateCw style={{ width: 13, height: 13 }} /> Try again
            </button>
            <button
              onClick={this.resetFiltersAndRetry}
              data-testid="route-error-reset-filters"
              title="Wipes saved lead/contact filters from localStorage, then retries. Often fixes blank pages caused by stale filter state."
              style={{ padding: "8px 14px", borderRadius: 8, border: "0.5px solid #ddd", background: "#fff", color: "var(--text-primary, #2b2b2f)", fontSize: 12, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}
            >
              <Filter style={{ width: 13, height: 13 }} /> Reset filters &amp; retry
            </button>
            <button
              onClick={this.goHome}
              data-testid="route-error-go-home"
              style={{ padding: "8px 14px", borderRadius: 8, border: "0.5px solid #ddd", background: "#fff", color: "var(--text-primary, #2b2b2f)", fontSize: 12, fontWeight: 500, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}
            >
              <Home style={{ width: 13, height: 13 }} /> Go to Dashboard
            </button>
          </div>

          <details style={{ borderTop: "0.5px solid #eee", padding: "10px 24px 16px" }}>
            <summary style={{ fontSize: 11, color: "var(--text-tertiary, #6E6E72)", cursor: "pointer", userSelect: "none" }} data-testid="route-error-details-toggle">
              Show technical details (for support)
            </summary>
            <div style={{ marginTop: 10, fontSize: 11, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", color: "#5B5B5F", background: "#f8f8f9", border: "0.5px solid #eee", borderRadius: 6, padding: 10, maxHeight: 200, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              <div><strong>{err?.name || "Error"}:</strong> {String(err?.message || err || "Unknown error")}</div>
              {err?.stack && (
                <div style={{ marginTop: 8, opacity: 0.75 }}>{String(err.stack).split("\n").slice(0, 8).join("\n")}</div>
              )}
            </div>
            <div style={{ marginTop: 8, fontSize: 10, color: "var(--text-tertiary, #888)" }}>
              Tip: copy the lines above and send them to support to speed up the fix.
            </div>
          </details>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
